import React, { useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  CircularProgress,
  InputAdornment,
  TextField,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import clienteService, { Cliente, PARAMS_CLIENTE_OPERATIVO } from '../../services/clienteService';
import { getApiErrorMessage } from '../../utils/apiError';

interface Props {
  value: string;
  onChange: (nroCliente: string) => void;
}

const PAGE_SIZE = 20;

const getClienteLabel = (cliente: Cliente): string => {
  const nombre = cliente.nombre?.trim() || cliente.razonSocial?.trim() || '';
  return nombre ? `${cliente.nroCliente} - ${nombre}` : String(cliente.nroCliente);
};

const ClienteSearchFilter: React.FC<Props> = ({ value, onChange }) => {
  const [inputValue, setInputValue] = useState('');
  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const searchRequestId = useRef(0);

  useEffect(() => {
    const requestId = ++searchRequestId.current;
    const term = inputValue.trim();
    if (!term || selectedCliente) {
      setClientes([]);
      setError('');
      setLoading(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      setLoading(true);
      setError('');
      clienteService.getClientes({
        searchTerm: term,
        pageNumber: 1,
        pageSize: PAGE_SIZE,
        ...PARAMS_CLIENTE_OPERATIVO,
      }).then((result) => {
        if (requestId === searchRequestId.current) {
          setClientes(result.items ?? []);
        }
      }).catch((err: unknown) => {
        if (requestId === searchRequestId.current) {
          setClientes([]);
          setError(getApiErrorMessage(err, 'No se pudo buscar clientes.'));
        }
      }).finally(() => {
        if (requestId === searchRequestId.current) setLoading(false);
      });
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [inputValue, selectedCliente]);

  return (
    <Autocomplete
      fullWidth
      size="small"
      options={clientes}
      value={selectedCliente}
      inputValue={inputValue}
      loading={loading}
      filterOptions={(options) => options}
      getOptionLabel={getClienteLabel}
      isOptionEqualToValue={(option, selected) => option.nroCliente === selected.nroCliente}
      noOptionsText={error || 'No se encontraron clientes'}
      onChange={(_, cliente) => {
        setSelectedCliente(cliente);
        setInputValue(cliente ? getClienteLabel(cliente) : '');
        setError('');
        onChange(cliente ? String(cliente.nroCliente) : '');
      }}
      onInputChange={(_, nextInputValue, reason) => {
        setInputValue(nextInputValue);
        if (reason === 'input') {
          if (selectedCliente) setSelectedCliente(null);
          if (value) onChange('');
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Cliente"
          placeholder="Buscar por nombre o número"
          error={Boolean(error)}
          helperText={error || undefined}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={18} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default ClienteSearchFilter;
